import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Instagram, Facebook, Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { CONTACT, CONTACT_SECTION } from "@/data/content";
import { t } from "@/data/texts";
import { Reveal } from "@/components/effects/Effects";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Vyplňte prosím jméno, e-mail a zprávu.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Zpráva odeslána. Ozveme se vám.");
      setForm({ name: "", email: "", phone: "", message: "", website: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Zprávu se nepodařilo odeslat.");
    } finally {
      setSending(false);
    }
  };

  const address = t("contact.address", CONTACT.address);
  const phone = t("contact.phone", CONTACT.phone);
  const email = t("contact.email", CONTACT.email);
  const phoneHref = `tel:${phone.replace(/[^+\d]/g, "")}`;

  return (
    <section id="kontakt" className="relative bg-[#060505] border-b border-white/10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 py-20 lg:py-28 grid lg:grid-cols-2 gap-14">
        <Reveal>
          <p className="text-xs tracking-[0.4em] text-nv uppercase mb-3">SPOJTE SE</p>
          <h2 className="font-head text-3xl sm:text-4xl text-neutral-100 mb-5 vhs-glitch">{CONTACT_SECTION.title}</h2>
          <p className="text-sm text-neutral-400 leading-relaxed mb-8">{t("contact.intro", CONTACT_SECTION.intro)}</p>

          <div className="space-y-4 text-sm">
            <a href={`https://maps.google.com/?q=${encodeURIComponent(address)}`} target="_blank" rel="noreferrer"
              className="flex items-start gap-3 text-neutral-300 hover:text-nv transition-colors" data-testid="contact-address">
              <MapPin className="w-5 h-5 text-nv shrink-0 mt-0.5" /> {address}
            </a>
            <a href={phoneHref} className="flex items-center gap-3 text-neutral-300 hover:text-nv transition-colors" data-testid="contact-phone">
              <Phone className="w-5 h-5 text-nv shrink-0" /> {phone}
            </a>
            <a href={`mailto:${email}`} className="flex items-center gap-3 text-neutral-300 hover:text-nv transition-colors" data-testid="contact-email">
              <Mail className="w-5 h-5 text-nv shrink-0" /> {email}
            </a>
          </div>

          <div className="flex gap-4 mt-6">
            <a href={CONTACT.instagram} target="_blank" rel="noreferrer" className="p-2 border border-white/10 hover:border-nv hover:text-nv text-neutral-400 vhs-btn" aria-label="Instagram">
              <Instagram className="w-5 h-5" />
            </a>
            <a href={CONTACT.facebook} target="_blank" rel="noreferrer" className="p-2 border border-white/10 hover:border-nv hover:text-nv text-neutral-400 vhs-btn" aria-label="Facebook">
              <Facebook className="w-5 h-5" />
            </a>
            <a href={CONTACT.tiktok} target="_blank" rel="noreferrer" className="p-2 border border-white/10 hover:border-nv hover:text-nv text-neutral-400 vhs-btn font-head text-sm flex items-center justify-center w-9" aria-label="TikTok">
              TT
            </a>
          </div>

          <div className="mt-8 border border-white/10 overflow-hidden grayscale hover:grayscale-0 transition-all duration-500">
            <iframe
              title="Mapa GATE ESCAPE"
              src="https://maps.google.com/maps?q=Obora%20125%20%C5%A0indelov%C3%A1%20Karlovy%20Vary&t=&z=12&ie=UTF8&iwloc=&output=embed"
              className="w-full h-56 border-0"
              loading="lazy"
            />
          </div>
        </Reveal>

        <Reveal delay={120}>
          <form onSubmit={submit} className="border border-white/10 bg-[#0b0a09] p-7 space-y-4" data-testid="contact-form">
            <h3 className="font-head text-xl text-neutral-100 mb-2">Kontaktní formulář</h3>
            {/* honeypot */}
            <input type="text" tabIndex={-1} autoComplete="off" value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              className="hidden" aria-hidden="true" />
            <div>
              <Label className="text-xs text-neutral-400">Jméno *</Label>
              <Input data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-none bg-black border-white/10 focus-visible:ring-nv mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-neutral-400">E-mail *</Label>
                <Input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="rounded-none bg-black border-white/10 focus-visible:ring-nv mt-1" />
              </div>
              <div>
                <Label className="text-xs text-neutral-400">Telefon</Label>
                <Input data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="rounded-none bg-black border-white/10 focus-visible:ring-nv mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-neutral-400">Zpráva *</Label>
              <Textarea data-testid="contact-message-input" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="rounded-none bg-black border-white/10 focus-visible:ring-nv mt-1" />
            </div>
            <button type="submit" disabled={sending} data-testid="contact-submit-btn"
              className="hud-btn vhs-btn w-full font-head tracking-[0.2em] py-3 flex items-center justify-center gap-2">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "ODESÍLÁM…" : "ODESLAT ZPRÁVU"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
